// src/components/layout/Footer.js
import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../services/firebase";

function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success" | "error", text: string }

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setMessage({ type: "error", text: "Merci de renseigner une adresse e-mail." });
      return;
    }

    // petite validation simple
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setMessage({ type: "error", text: "Adresse e-mail invalide." });
      return;
    }

    try {
      setLoading(true);

      await addDoc(collection(db, "newsletter_prosoft"), {
        email: trimmed,
        createdAt: serverTimestamp(),
        source: "footer",
      });

      setMessage({
        type: "success",
        text: "Merci ! Votre adresse a bien été enregistrée.",
      });
      setEmail("");
    } catch (err) {
      console.error("Erreur enregistrement newsletter :", err);
      setMessage({
        type: "error",
        text: "Une erreur est survenue. Veuillez réessayer plus tard.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="site-footer mt-auto">
      {/* Panneau centré comme le contenu */}
      <div className="footer-panel">
        {/* Bandeau avantages */}
        <div className="border-top border-bottom bg-white">
          <div className="container py-3">
            <div className="row text-center small">
              <div className="col-6 col-md-3 mb-2 mb-md-0">
                <div className="fw-semibold">
                  <span className="me-2">🚚</span> Livraison rapide
                </div>
              </div>
              <div className="col-6 col-md-3 mb-2 mb-md-0">
                <div className="fw-semibold">
                  <span className="me-2">🕒</span> Support du lundi au samedi
                </div>
              </div>
              <div className="col-6 col-md-3 mb-2 mb-md-0">
                <div className="fw-semibold">
                  <span className="me-2">🔒</span> Paiement sécurisé
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bloc principal */}
        <div className="border-bottom bg-white">
          <div className="container py-4">
            <div className="row small">
              {/* Colonne : Qui sommes-nous ? */}
              <div className="col-md-4 mb-4 mb-md-0">
                <h6 className="fw-bold mb-2">Qui sommes-nous ?</h6>
                <p className="mb-2 text-muted">
                  ProSoft Tools est une boutique spécialisée dans les{" "}
                  <strong>imprimantes</strong>,{" "}
                  <strong>rouleaux de papier</strong>,{" "}
                  <strong>TPE</strong> et <strong>systèmes EPOS</strong>.
                </p>
                <p className="mb-2 text-muted">
                  Nous vous accompagnons dans la mise en place de solutions
                  d&apos;encaissement fiables au meilleur rapport
                  qualité&nbsp;/&nbsp;prix.
                </p>

                <p className="mb-1 text-muted">
                  📞{" "}
                  <span className="fw-semibold">+228 93 36 25 25</span>
                </p>
                <p className="mb-1 text-muted">
                  💬{" "}
                  <a
                    href="https://wa.me/22893362525?text=Bonjour,%20je%20souhaite%20des%20informations%20sur%20vos%20imprimantes%20et%20TPE."
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-none"
                  >
                    Contactez-nous sur WhatsApp
                  </a>
                </p>
                <p className="mb-0 text-muted">
                  ✉️{" "}
                  <a
                    href="mailto:info@prosoft-togo.com"
                    className="text-decoration-none"
                  >
                    info@prosoft-togo.com
                  </a>
                </p>
              </div>

              {/* Colonne : Mon compte */}
              <div className="col-md-3 mb-4 mb-md-0">
                <h6 className="fw-bold mb-2">Mon compte</h6>
                <ul className="list-unstyled mb-0">
                  <li className="mb-1">
                    <a
                      href="/mon-compte/commandes"
                      className="text-muted text-decoration-none"
                    >
                      ▸ Commandes
                    </a>
                  </li>
                  <li className="mb-1">
                    <a
                      href="/mon-compte/tableau-de-bord"
                      className="text-muted text-decoration-none"
                    >
                      ▸ Tableau de bord
                    </a>
                  </li>
                  <li className="mb-1">
                    <a
                      href="/mon-compte/parametres"
                      className="text-muted text-decoration-none"
                    >
                      ▸ Paramètres du compte
                    </a>
                  </li>
                  <li className="mb-1">
                    <a
                      href="/connexion"
                      className="text-muted text-decoration-none"
                    >
                      ▸ Connexion / Déconnexion
                    </a>
                  </li>
                </ul>
              </div>

              {/* Colonne : Newsletter */}
              <div className="col-md-5">
                <h6 className="fw-bold mb-2">
                  Abonnez-vous à notre newsletter
                </h6>
                <p className="small text-muted mb-3">
                  Recevez les dernières mises à jour sur nos{" "}
                  <strong>imprimantes</strong>, <strong>TPE</strong> et{" "}
                  <strong>solutions EPOS</strong>, ainsi que nos offres
                  spéciales.
                </p>

                <form
                  className="d-flex flex-column flex-sm-row gap-2"
                  onSubmit={handleNewsletterSubmit}
                >
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    placeholder="Entrez votre adresse e-mail"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-3"
                    disabled={loading}
                  >
                    {loading ? "Enregistrement..." : "S'INSCRIRE"}
                  </button>
                </form>

                {message && (
                  <div
                    className={`small mt-2 ${
                      message.type === "success"
                        ? "text-success"
                        : "text-danger"
                    }`}
                  >
                    {message.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bas de page */}
        <div className="bg-dark text-light small py-2">
          <div className="container d-flex flex-column flex-md-row justify-content-between align-items-center">
            <div>
              Copyright {new Date().getFullYear()} ©{" "}
              <span className="fw-semibold">ProSoft Tools</span>
            </div>
            <div className="mt-1 mt-md-0 text-muted">
              Solutions d&apos;encaissement • Imprimantes • TPE • EPOS
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
